import {Injectable} from '@angular/core';
import {TeardownReport} from '../models';
import {BehaviorSubject, Observable} from 'rxjs';
import {FilesSocket} from '../sockets';
import {TorchTokenService} from '../../@users';
import {SessionService} from '../../../../app/shared/services/session.service';
import {Burly} from 'kb-burly';
import {TorchAPIConfiguration} from '../../../config/api.configuration';

@Injectable({
  providedIn: 'root'
})
export class TorchOptixService {
  teardownSocketConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  teardownsSocket: FilesSocket;
  teardownsError: BehaviorSubject<{ status: string; message: string }> = new BehaviorSubject<{ status: string; message: string }>(null);
  teardownsReceived: BehaviorSubject<{ zipName: string }> = new BehaviorSubject<{ zipName: string }>(null);


  private torchToken: string;
  private teardownAwaitTimeout = 5000;

  constructor(private tokenService: TorchTokenService,
              private config: TorchAPIConfiguration,
              private sessionService: SessionService) {
   this.fetchTorchToken();
  }

  public getOptixFileDownloadURL(optixID: string): string {
    return Burly(this.config.filesUrl)
      .addSegment('/optix')
      .addSegment(`/download/${optixID}`)
      .get;
  }

  public getOptixFileViewURL(optixID: string): string {
    return Burly(this.config.filesUrl)
      .addSegment('/optix')
      .addSegment(`/download/${optixID}`)
      .get;
  }

  public getOptixExportDownloadURL(zipName: string) {
    return Burly(this.config.filesUrl)
      .addSegment('/optix/export/download')
      .addQuery('zipFileName', zipName)
      .get;
  }

  public getOptixMultipleDownloadURL(optixIDs: string[], fileNames: string[]) {
    return Burly(this.config.filesUrl)
      .addSegment('/optix')
      .addSegment('/multiple')
      .addSegment('/download')
      .addQuery('ids', optixIDs.join(','))
      .addQuery('fileNames', fileNames.join(','))
      .get;
  }

  public cancelTeardownsRequest() {
    this.teardownsSocket.emit('cancelRequest');
    this.disconnectTeardownSocket();
    this.setupSocketListeners();
    this.connectTeardownSocket();
  }

  public deleteTeardownDocuments() {
    setTimeout(() => {
      this.teardownsSocket.emit('deleteDownloads');
    }, 10000);
  }

  public submitTeardownsRequest(teardownReports: TeardownReport[]) {
    const documents: { id: string; fileName?: string }[] = teardownReports.map(report => ({
      id: `${report.optixID}`,
      fileName: report.poSONum
    }));

    this.teardownsSocket.emit('downloadDocuments', {
      documents
    });

    return new Observable(subscriber => {
      // Set timeout to keep request from taking forever
      let receivedRequest = false;

      setTimeout(() => {
        if (!receivedRequest) {
          this.teardownsError.next({status: 'timeoutError', message: 'Request took too long to be processed'});
          this.disconnectTeardownSocket();
          this.setupSocketListeners();
          this.connectTeardownSocket();
        }
      }, this.teardownAwaitTimeout);

      this.teardownsSocket.once('downloadDocumentRequestReceived', (payload) => {
        receivedRequest = true;
        subscriber.next(payload);
        subscriber.complete();
      });
    });
  }

  public connectTeardownSocket() {
    this.teardownsSocket.connect();
  }

  public disconnectTeardownSocket() {
    this.teardownsSocket.disconnect();
  }

  public resetDownloadState() {
    this.teardownsReceived.next(null);
    this.teardownsError.next(null);
  }

  private setupSocketListeners() {
    this.teardownsSocket.on('connect', () => {
      this.teardownSocketConnected.next(true);
    });

    this.teardownsSocket.on('disconnect', () => {
      this.teardownSocketConnected.next(false);
    });

    this.teardownsSocket.on('exception', (error: { status: string; message: string }) => {
      this.teardownsError.next(error);
    });

    this.teardownsSocket.on('finishedDownloading', (payload) => {
      this.teardownsReceived.next(payload);
    });
  }

  private fetchTorchToken() {
    this.tokenService.currentTokens.subscribe(tokens => {
      if (!!tokens) {
        this.torchToken = tokens.torchToken;
        this.setupTeardownSocket(tokens.torchToken);
      }
    });
  }

  private setupTeardownSocket(token: string) {
    const socketWasConnected = this.teardownSocketConnected.value;

    if (!!this.teardownsSocket && this.teardownsSocket.token !== token) {
      if (socketWasConnected) {
        this.teardownsSocket.disconnect();
      }

      this.teardownsSocket = new FilesSocket(token, this.sessionService.filesSocketBackend);
    } else if (!!!this.teardownsSocket) {
      this.teardownsSocket = new FilesSocket(token, this.sessionService.filesSocketBackend);
    }
    this.setupSocketListeners();

    if (socketWasConnected) {
      this.connectTeardownSocket();
    }
  }
}
